import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatEUR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Users, Package, ShoppingCart, BadgePercent, Trash2, Loader2, UploadCloud, Check, X, Mail, Building2, Plus, Truck, Calendar as CalendarIcon, Sun, Moon, Share2, Copy, KeyRound, TrendingUp, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import PushNotificationsToggle from "../components/PushNotificationsToggle";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { format, addDays } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { toast } from "sonner";

const Admin = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" />;
  if (user.role !== "admin" && user.role !== "staff") return <Navigate to="/dashboard" />;
  const isAdmin = user.role === "admin";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="mb-10">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#00A3FF] mb-3">
          {isAdmin ? "Area Amministratore" : "Area Operatore"}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-light text-[#032B44]">
          {isAdmin ? "Dashboard Admin" : "Dashboard Operatore"}
        </h1>
      </div>

      <div className="mb-6">
        <PushNotificationsToggle />
      </div>

      {isAdmin && <Stats />}

      <Tabs defaultValue="schedule" className="mt-12">
        <TabsList className="bg-sky-50 p-1 rounded-lg overflow-x-auto flex-nowrap max-w-full">
          <TabsTrigger value="schedule" data-testid="tab-schedule"><Truck className="w-4 h-4 mr-1.5" /> Consegne</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders"><ShoppingCart className="w-4 h-4 mr-1.5" /> Ordini</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-1.5" /> Utenti & Prezzi</TabsTrigger>}
          {isAdmin && <TabsTrigger value="revenue" data-testid="tab-revenue"><TrendingUp className="w-4 h-4 mr-1.5" /> Ricavi</TabsTrigger>}
          {isAdmin && <TabsTrigger value="products" data-testid="tab-products"><Package className="w-4 h-4 mr-1.5" /> Prodotti</TabsTrigger>}
          {isAdmin && <TabsTrigger value="partners" data-testid="tab-partners"><Building2 className="w-4 h-4 mr-1.5" /> Richieste Partner</TabsTrigger>}
        </TabsList>
        <TabsContent value="schedule" className="mt-6"><ScheduleTab /></TabsContent>
        <TabsContent value="orders" className="mt-6"><OrdersTab isAdmin={isAdmin} /></TabsContent>
        {isAdmin && <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>}
        {isAdmin && <TabsContent value="revenue" className="mt-6"><RevenueTab /></TabsContent>}
        {isAdmin && <TabsContent value="products" className="mt-6"><ProductsTab /></TabsContent>}
        {isAdmin && <TabsContent value="partners" className="mt-6"><PartnersTab /></TabsContent>}
      </Tabs>
    </div>
  );
};

const Stats = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Stat label="Utenti" value={s.total_users} />
      <Stat label="Ordini" value={s.total_orders} />
      <Stat label="Pagati" value={s.paid_orders} />
      <Stat label="Ricavi" value={formatEUR(s.revenue_eur)} accent />
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-2xl p-5 border ${accent ? "border-sky-200 bg-gradient-to-br from-[#032B44] to-[#00A3FF] text-white" : "border-sky-100 bg-white"}`}>
    <p className={`text-xs tracking-[0.2em] uppercase font-bold ${accent ? "opacity-80" : "text-sky-600"}`}>{label}</p>
    <p className="font-heading text-3xl font-semibold mt-1">{value}</p>
  </div>
);

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [savingRole, setSavingRole] = useState(null);

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  };
  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => {
    if (u.role === role) return;
    if (role === "admin" && !window.confirm(`Sei sicuro di voler rendere ${u.name} amministratore? Avrà accesso totale (ricavi, utenti, prodotti).`)) return;
    setSavingRole(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/role`, { role });
      toast.success(`Ruolo aggiornato: ${role}`);
      load();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally { setSavingRole(null); }
  };

  const RolePicker = ({ u, mobile }) => (
    <select
      value={u.role}
      onChange={(e) => setRole(u, e.target.value)}
      disabled={savingRole === u.user_id}
      className={`text-[10px] tracking-wider uppercase font-bold px-2 py-1 rounded-full border bg-white cursor-pointer ${
        u.role === "admin" ? "border-sky-300 text-sky-700"
        : u.role === "staff" ? "border-amber-300 text-amber-700"
        : "border-slate-200 text-slate-600"
      } ${savingRole === u.user_id ? "opacity-50" : ""}`}
      data-testid={`role-picker-${mobile ? "mobile-" : ""}${u.user_id}`}
    >
      <option value="customer">Cliente</option>
      <option value="staff">Operatore</option>
      <option value="admin">Admin</option>
    </select>
  );

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-sky-100 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sky-50/60 text-left">
            <tr>
              <th className="px-5 py-3 font-medium text-[#032B44]">Utente</th>
              <th className="px-5 py-3 font-medium text-[#032B44]">Email</th>
              <th className="px-5 py-3 font-medium text-[#032B44]">Ruolo</th>
              <th className="px-5 py-3 font-medium text-[#032B44]">Prezzi custom</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sky-50">
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {u.picture && <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />}
                    <span className="font-medium text-[#032B44]">{u.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-600">
                  {u.email}
                  {u.has_password === false && (
                    <span className="ml-2 text-[9px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Google</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <RolePicker u={u} />
                </td>
                <td className="px-5 py-3 text-[#032B44] font-medium">{u.custom_price_count}</td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" className="border-sky-200 text-[#032B44]" onClick={() => setResetting(u)} data-testid={`reset-password-${u.user_id}`}>
                      <KeyRound className="w-4 h-4 mr-1" /> Reset password
                    </Button>
                    <Button size="sm" variant="outline" className="border-sky-200 text-[#032B44]" onClick={() => setEditing(u)} data-testid={`edit-prices-${u.user_id}`}>
                      <BadgePercent className="w-4 h-4 mr-1" /> Prezzi
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8 text-slate-500">Nessun utente registrato.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {users.map((u) => (
          <div key={u.user_id} className="bg-white border border-sky-100 rounded-2xl p-4" data-testid={`admin-user-card-${u.user_id}`}>
            <div className="flex items-start gap-3 mb-3">
              {u.picture
                ? <img src={u.picture} alt="" className="w-10 h-10 rounded-full shrink-0" />
                : <div className="w-10 h-10 rounded-full bg-sky-100 grid place-items-center shrink-0 text-sky-700 font-semibold">{(u.name || u.email)[0]?.toUpperCase()}</div>}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#032B44] truncate">{u.name}</p>
                <p className="text-xs text-slate-500 truncate">{u.email}</p>
              </div>
              <span className="shrink-0">
                <RolePicker u={u} mobile />
              </span>
            </div>
            <div className="flex items-center justify-between text-xs mb-3">
              <span className="text-slate-500">Prezzi custom: <strong className="text-[#032B44]">{u.custom_price_count}</strong></span>
              {u.has_password === false && (
                <span className="text-[9px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Google</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" className="border-sky-200 text-[#032B44]" onClick={() => setEditing(u)} data-testid={`edit-prices-mobile-${u.user_id}`}>
                <BadgePercent className="w-4 h-4 mr-1" /> Prezzi
              </Button>
              <Button size="sm" variant="outline" className="border-sky-200 text-[#032B44]" onClick={() => setResetting(u)} data-testid={`reset-password-mobile-${u.user_id}`}>
                <KeyRound className="w-4 h-4 mr-1" /> Password
              </Button>
            </div>
          </div>
        ))}
        {users.length === 0 && (
          <div className="text-center py-8 text-slate-500 bg-white border border-sky-100 rounded-2xl">Nessun utente registrato.</div>
        )}
      </div>

      {editing && (
        <PriceManager
          user={editing}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); }}
        />
      )}
      {resetting && (
        <AdminResetPasswordDialog
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => { setResetting(null); load(); }}
        />
      )}
    </>
  );
};

const AdminResetPasswordDialog = ({ user, onClose, onDone }) => {
  const [pw, setPw] = useState(() => {
    // Suggest a strong random default
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let s = "";
    for (let i = 0; i < 10; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return s;
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Minimo 6 caratteri"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.user_id}/reset-password`, { new_password: pw });
      setDone(true);
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally { setBusy(false); }
  };

  const copyAll = async () => {
    const text = `Ciao ${user.name.split(" ")[0] || ""},\n\nLa tua password EasyIce è stata reimpostata:\n\nEmail: ${user.email}\nPassword temporanea: ${pw}\n\nTi consigliamo di cambiarla al primo accesso.`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Messaggio copiato", { description: "Incollalo su WhatsApp/SMS per il cliente" });
    } catch {
      toast.info("Copia manualmente il testo visualizzato");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="admin-reset-password-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-sky-50 grid place-items-center mb-3">
            <KeyRound className="w-5 h-5 text-sky-700" />
          </div>
          <DialogTitle className="font-heading text-2xl text-[#032B44]">
            {done ? "Password reimpostata" : "Reset password"}
          </DialogTitle>
          <p className="text-sm text-slate-600">
            {done
              ? `Nuova password impostata per ${user.email}. Comunicala al cliente e ricordagli di cambiarla al primo accesso.`
              : <>Imposta una password temporanea per <strong>{user.name}</strong> ({user.email}).</>}
          </p>
        </DialogHeader>

        {done ? (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-emerald-700 mb-2">Password da comunicare</p>
              <p className="font-mono text-lg text-emerald-900 select-all break-all" data-testid="admin-reset-new-pw">{pw}</p>
            </div>
            <Button onClick={copyAll} className="w-full bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="admin-reset-copy">
              <Copy className="w-4 h-4 mr-2" /> Copia messaggio pronto per WhatsApp
            </Button>
            <Button variant="outline" onClick={onDone} className="w-full border-sky-200" data-testid="admin-reset-close">
              Chiudi
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs tracking-wider uppercase font-bold text-sky-600 block mb-1.5">Nuova password</label>
              <Input value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} className="font-mono h-11" data-testid="admin-reset-input" />
              <p className="text-[11px] text-slate-500 mt-1">Password suggerita generata casualmente. Puoi modificarla.</p>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={onClose} className="border-sky-200">Annulla</Button>
              <Button type="submit" disabled={busy} className="bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="admin-reset-submit">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio…</> : "Reimposta"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PriceManager = ({ user, products, onClose, onSaved }) => {
  const [cps, setCps] = useState([]);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);

  const load = () => {
    api.get(`/admin/users/${user.user_id}/custom-prices`).then((r) => {
      setCps(r.data);
      const d = {};
      r.data.forEach((c) => { d[c.product_id] = c.custom_price; });
      setDraft(d);
    });
  };
  useEffect(() => { load(); }, [user.user_id]);

  const save = async (product_id) => {
    const val = parseFloat(draft[product_id]);
    if (isNaN(val) || val < 0) { toast.error("Prezzo non valido"); return; }
    setSaving(product_id);
    try {
      await api.post("/admin/custom-prices", { user_id: user.user_id, product_id, custom_price: val });
      toast.success("Prezzo aggiornato");
      load(); onSaved();
    } finally { setSaving(null); }
  };
  const remove = async (product_id) => {
    setSaving(product_id);
    try {
      await api.delete(`/admin/custom-prices?user_id=${user.user_id}&product_id=${product_id}`);
      const d = { ...draft }; delete d[product_id]; setDraft(d);
      toast.success("Prezzo rimosso");
      load(); onSaved();
    } finally { setSaving(null); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="price-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl sm:text-2xl text-[#032B44]">Prezzi personalizzati — {user.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {products.map((p) => {
            const has = cps.some((c) => c.product_id === p.product_id);
            return (
              <div key={p.product_id} className="p-3 border border-sky-100 rounded-lg" data-testid={`price-row-${p.slug}`}>
                <div className="flex items-center gap-3 mb-2">
                  <img src={p.image_url} alt="" className="w-10 h-10 object-cover rounded-md shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#032B44] truncate">{p.name}</p>
                    <p className="text-xs text-slate-500">Prezzo base: {formatEUR(p.base_price)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">€</span>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      value={draft[p.product_id] ?? ""}
                      onChange={(e) => setDraft({ ...draft, [p.product_id]: e.target.value })}
                      placeholder={p.base_price.toFixed(2)}
                      className="pl-7 h-10"
                      data-testid={`price-input-${p.slug}`}
                    />
                  </div>
                  <Button size="sm" onClick={() => save(p.product_id)} disabled={saving === p.product_id} className="bg-[#032B44] hover:bg-[#00A3FF] text-white shrink-0" data-testid={`price-save-${p.slug}`}>
                    {saving === p.product_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Salva"}
                  </Button>
                  {has && (
                    <Button size="sm" variant="ghost" onClick={() => remove(p.product_id)} className="text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0 px-2" data-testid={`price-delete-${p.slug}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-sky-200 w-full sm:w-auto">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const OrdersTab = ({ isAdmin = true }) => {
  const [orders, setOrders] = useState([]);
  const [acting, setActing] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const markPaid = async (id) => {
    setActing(id);
    try {
      await api.post(`/admin/orders/${id}/mark-paid`);
      toast.success("Ordine segnato come pagato");
      load();
    } finally { setActing(null); }
  };

  const cancelOrder = async (o) => {
    const confirmMsg = o.status === "paid"
      ? `Annullare l'ordine PAGATO #${o.order_id.slice(-8)} di ${o.user_name}?\n\nRicorda di fare il rimborso manualmente dal tuo pannello PayPal.`
      : `Annullare l'ordine #${o.order_id.slice(-8)} di ${o.user_name}?`;
    if (!window.confirm(confirmMsg)) return;
    setActing(o.order_id);
    try {
      await api.post(`/admin/orders/${o.order_id}/cancel`);
      toast.success("Ordine annullato");
      load();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally { setActing(null); }
  };

  const statusStyle = (s) => ({
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    awaiting_delivery: "bg-indigo-50 text-indigo-700 border-indigo-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  }[s] || "bg-slate-100 text-slate-600 border-slate-200");

  const statusLabel = (s) => ({
    paid: "Pagato",
    pending: "In attesa",
    awaiting_delivery: "Alla consegna",
    cancelled: "Annullato",
  }[s] || s);

  const counts = {
    all: orders.length,
    pending: orders.filter((o) => o.status === "pending" || o.status === "awaiting_delivery").length,
    paid: orders.filter((o) => o.status === "paid").length,
    cancelled: orders.filter((o) => o.status === "cancelled").length,
  };

  const filtered = filter === "all"
    ? orders
    : filter === "pending"
      ? orders.filter((o) => o.status === "pending" || o.status === "awaiting_delivery")
      : orders.filter((o) => o.status === filter);

  const FilterBtn = ({ value, label }) => (
    <button
      onClick={() => setFilter(value)}
      data-testid={`orders-filter-${value}`}
      className={`px-3 h-9 rounded-lg text-sm font-medium transition-colors border ${
        filter === value
          ? "bg-[#032B44] text-white border-[#032B44]"
          : "bg-white text-[#032B44] border-sky-200 hover:bg-sky-50"
      }`}
    >
      {label} <span className={`ml-1.5 text-xs ${filter === value ? "text-sky-200" : "text-slate-500"}`}>{counts[value]}</span>
    </button>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="orders-filters">
        <FilterBtn value="all" label="Tutti" />
        <FilterBtn value="pending" label="In attesa" />
        <FilterBtn value="paid" label="Pagati" />
        <FilterBtn value="cancelled" label="Annullati" />
      </div>
      <div className="bg-white border border-sky-100 rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-sky-50/60 text-left">
          <tr>
            <th className="px-5 py-3 font-medium text-[#032B44]">Ordine</th>
            <th className="px-5 py-3 font-medium text-[#032B44]">Cliente</th>
            <th className="px-5 py-3 font-medium text-[#032B44]">Consegna</th>
            <th className="px-5 py-3 font-medium text-[#032B44]">Pagamento</th>
            <th className="px-5 py-3 font-medium text-[#032B44]">Totale</th>
            <th className="px-5 py-3 font-medium text-[#032B44]">Stato</th>
            <th className="px-5 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-sky-50">
          {filtered.map((o) => (
            <tr key={o.order_id} data-testid={`admin-order-${o.order_id}`}>
              <td className="px-5 py-3 font-mono text-xs text-slate-600">#{o.order_id.slice(-8)}</td>
              <td className="px-5 py-3">
                <p className="font-medium text-[#032B44]">{o.user_name}</p>
                <p className="text-xs text-slate-500">{o.user_phone || o.user_email}</p>
              </td>
              <td className="px-5 py-3">
                <p className="text-slate-700 capitalize">{o.delivery_slot || "—"}</p>
                <p className="text-[10px] text-slate-500">{o.delivery_date ? format(new Date(o.delivery_date), "d MMM yyyy", { locale: itLocale }) : ""}</p>
                <p className="text-[10px] text-slate-500 truncate max-w-[180px]">{o.delivery_address || ""}</p>
              </td>
              <td className="px-5 py-3 text-slate-700">
                {o.payment_method === "cash" ? "Contanti" : o.payment_method === "card" ? "Carta" : o.payment_method === "paypal" ? "PayPal" : "—"}
              </td>
              <td className="px-5 py-3 font-semibold text-[#032B44]">{formatEUR(o.total)}</td>
              <td className="px-5 py-3">
                <span className={`text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 rounded-full border ${statusStyle(o.status)}`}>
                  {statusLabel(o.status)}
                </span>
              </td>
              <td className="px-5 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  {o.status === "awaiting_delivery" && (
                    <Button size="sm" onClick={() => markPaid(o.order_id)} disabled={acting === o.order_id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`mark-paid-${o.order_id}`}>
                      {acting === o.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Segna pagato</>}
                    </Button>
                  )}
                  {o.status !== "cancelled" && isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => cancelOrder(o)} disabled={acting === o.order_id} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`cancel-order-${o.order_id}`}>
                      {acting === o.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Annulla"}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan="7" className="text-center py-8 text-slate-500">
              {orders.length === 0 ? "Nessun ordine." : "Nessun ordine in questa categoria."}
            </td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};

const RevenueTab = () => {
  const [view, setView] = useState("month"); // month | quarter | year
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const loadData = async () => {
    setLoadingData(true);
    try {
      const params = view === "year" ? { period: "year" } : { period: view, year };
      const r = await api.get("/admin/revenue", { params });
      setData(r.data);
    } finally { setLoadingData(false); }
  };
  const loadRefunds = async () => {
    const r = await api.get("/admin/refunds/pending");
    setRefunds(r.data);
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [view, year]);
  useEffect(() => { loadRefunds(); }, []);

  const confirmRefund = async (o) => {
    if (!window.confirm(`Confermi di aver rimborsato ${formatEUR(o.total)} al cliente ${o.user_name || o.user_email}?\n\nQuesta azione non si può annullare.`)) return;
    setConfirming(o.order_id);
    try {
      await api.post(`/admin/orders/${o.order_id}/confirm-refund`);
      toast.success("Rimborso confermato");
      loadRefunds();
      loadData();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally { setConfirming(null); }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="space-y-6" data-testid="revenue-tab">
      {/* Pending refunds banner */}
      {refunds.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5" data-testid="refunds-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 grid place-items-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-heading text-base font-semibold text-red-900">
                {refunds.length === 1 ? "1 rimborso da gestire" : `${refunds.length} rimborsi da gestire`}
              </p>
              <p className="text-sm text-red-700 mt-0.5">
                Questi ordini sono stati annullati ma erano già stati pagati. Rimborsa manualmente dal pannello PayPal, poi conferma qui sotto.
              </p>
              <div className="mt-4 space-y-2">
                {refunds.map((o) => (
                  <div key={o.order_id} className="bg-white rounded-lg p-3 border border-red-100 flex items-center gap-3 flex-wrap" data-testid={`refund-item-${o.order_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs text-slate-500">#{o.order_id.slice(-8)}</p>
                      <p className="text-sm font-medium text-[#032B44] truncate">{o.user_name || o.user_email}</p>
                      <p className="text-xs text-slate-500">
                        Annullato il {o.cancelled_at ? format(new Date(o.cancelled_at), "d MMM yyyy HH:mm", { locale: itLocale }) : "—"}
                        {o.paypal_order_id && <span> · PayPal: <span className="font-mono">{o.paypal_order_id.slice(-10)}</span></span>}
                      </p>
                    </div>
                    <p className="font-heading text-lg font-semibold text-red-700">{formatEUR(o.total)}</p>
                    <Button
                      size="sm"
                      onClick={() => confirmRefund(o)}
                      disabled={confirming === o.order_id}
                      className="bg-[#032B44] hover:bg-[#00A3FF] text-white"
                      data-testid={`confirm-refund-${o.order_id}`}
                    >
                      {confirming === o.order_id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <><Check className="w-3.5 h-3.5 mr-1" /> Ho rimborsato</>}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View switcher */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="revenue-view-switcher">
        {[
          { v: "month", label: "Per mese" },
          { v: "quarter", label: "Per trimestre" },
          { v: "year", label: "Storico anni" },
        ].map(({ v, label }) => (
          <button
            key={v}
            onClick={() => setView(v)}
            data-testid={`revenue-view-${v}`}
            className={`px-4 h-10 rounded-lg text-sm font-medium transition-colors border ${
              view === v
                ? "bg-[#032B44] text-white border-[#032B44]"
                : "bg-white text-[#032B44] border-sky-200 hover:bg-sky-50"
            }`}
          >
            {label}
          </button>
        ))}
        {view !== "year" && (
          <div className="ml-auto flex items-center gap-1 bg-white border border-sky-200 rounded-lg h-10 px-2">
            <button onClick={() => setYear((y) => y - 1)} className="w-7 h-7 grid place-items-center rounded hover:bg-sky-50" data-testid="revenue-prev-year">
              <ChevronLeft className="w-4 h-4 text-[#032B44]" />
            </button>
            <span className="font-heading text-base font-medium text-[#032B44] px-2" data-testid="revenue-current-year">{year}</span>
            <button onClick={() => setYear((y) => y + 1)} disabled={year >= currentYear} className="w-7 h-7 grid place-items-center rounded hover:bg-sky-50 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="revenue-next-year">
              <ChevronRight className="w-4 h-4 text-[#032B44]" />
            </button>
          </div>
        )}
      </div>

      {loadingData && (
        <div className="py-12 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      )}

      {!loadingData && data && data.period === "month" && view === "month" && (
        <RevenueMonthly data={data} />
      )}
      {!loadingData && data && data.period === "quarter" && view === "quarter" && (
        <RevenueQuarterly data={data} />
      )}
      {!loadingData && data && data.period === "year" && view === "year" && (
        <RevenueYearly data={data} onPick={(y) => { setYear(y); setView("month"); }} />
      )}
    </div>
  );
};

const RevenueBar = ({ value, max }) => {
  const w = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex-1 h-2 bg-sky-50 rounded-full overflow-hidden">
      <div className="h-full bg-gradient-to-r from-[#00A3FF] to-[#032B44]" style={{ width: `${w}%` }} />
    </div>
  );
};

const RevenueMonthly = ({ data }) => {
  const max = Math.max(...data.months.map((m) => m.total));
  return (
    <div className="bg-white border border-sky-100 rounded-2xl overflow-hidden" data-testid="revenue-monthly">
      <div className="px-6 py-5 bg-gradient-to-r from-[#032B44] to-[#00A3FF] text-white flex items-center justify-between">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-sky-200">Anno {data.year}</p>
          <p className="font-heading text-3xl font-light mt-1" data-testid="revenue-year-total">{formatEUR(data.year_total)}</p>
          <p className="text-xs text-sky-200 mt-1">{data.year_count} {data.year_count === 1 ? "ordine pagato" : "ordini pagati"}</p>
        </div>
        <TrendingUp className="w-10 h-10 text-white/30" />
      </div>
      <div className="divide-y divide-sky-50">
        {data.months.map((m) => (
          <div key={m.month} className="px-6 py-3 flex items-center gap-4" data-testid={`revenue-month-${m.month}`}>
            <p className="w-24 text-sm text-slate-600">{m.label}</p>
            <RevenueBar value={m.total} max={max} />
            <p className="w-16 text-xs text-slate-500 text-right">{m.count}</p>
            <p className="w-24 font-heading font-semibold text-[#032B44] text-right">{formatEUR(m.total)}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const RevenueQuarterly = ({ data }) => {
  const max = Math.max(...data.quarters.map((q) => q.total));
  const quarterRanges = ["Gen – Mar", "Apr – Giu", "Lug – Set", "Ott – Dic"];
  return (
    <div className="space-y-4" data-testid="revenue-quarterly">
      <div className="bg-gradient-to-r from-[#032B44] to-[#00A3FF] text-white rounded-2xl px-6 py-5">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-sky-200">Anno {data.year} · Totale</p>
        <p className="font-heading text-3xl font-light mt-1">{formatEUR(data.year_total)}</p>
        <p className="text-xs text-sky-200 mt-1">{data.year_count} {data.year_count === 1 ? "ordine pagato" : "ordini pagati"}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.quarters.map((q, idx) => (
          <div key={q.quarter} className="bg-white border border-sky-100 rounded-2xl p-5" data-testid={`revenue-quarter-${q.quarter}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#00A3FF]">{q.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{quarterRanges[idx]}</p>
              </div>
              <p className="font-heading text-2xl font-semibold text-[#032B44]">{formatEUR(q.total)}</p>
            </div>
            <RevenueBar value={q.total} max={max} />
            <p className="text-xs text-slate-500 mt-2">{q.count} {q.count === 1 ? "ordine" : "ordini"}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const RevenueYearly = ({ data, onPick }) => {
  if (data.years.length === 0) {
    return (
      <div className="bg-white border border-sky-100 rounded-2xl p-10 text-center text-slate-500" data-testid="revenue-yearly-empty">
        Nessun ricavo registrato. Gli ordini pagati appariranno qui raggruppati per anno.
      </div>
    );
  }
  const max = Math.max(...data.years.map((y) => y.total));
  return (
    <div className="space-y-3" data-testid="revenue-yearly">
      {data.years.map((y) => (
        <button
          key={y.year}
          onClick={() => onPick(y.year)}
          className="w-full bg-white border border-sky-100 rounded-2xl p-5 text-left hover:border-[#00A3FF] hover:bg-sky-50/50 transition-colors group"
          data-testid={`revenue-year-${y.year}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-heading text-2xl font-light text-[#032B44]">{y.year}</p>
              <p className="text-xs text-slate-500 mt-0.5">{y.count} {y.count === 1 ? "ordine pagato" : "ordini pagati"}</p>
            </div>
            <p className="font-heading text-3xl font-semibold text-[#032B44] group-hover:text-[#00A3FF]">{formatEUR(y.total)}</p>
          </div>
          <RevenueBar value={y.total} max={max} />
          <p className="text-[11px] text-sky-600 mt-2 font-medium">Clicca per vedere il dettaglio mensile →</p>
        </button>
      ))}
    </div>
  );
};

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const syncToSeed = async () => {
    if (!window.confirm("Aggiornare i prodotti esistenti (nome, descrizione, prezzo base, immagine, unità) con i valori più recenti definiti nel codice?\n\nUtile dopo un deploy se i prodotti in DB hanno ancora valori vecchi.")) return;
    setSyncing(true);
    try {
      const r = await api.post("/admin/products/sync-seed");
      toast.success(`${r.data.count} prodotti aggiornati`, {
        description: r.data.results.map((x) => `${x.slug}: ${x.action}`).join(" · "),
      });
      load();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally { setSyncing(false); }
  };

  const updatePrice = async (p, newPrice) => {
    const val = parseFloat(newPrice);
    if (isNaN(val) || val < 0) return;
    await api.put(`/admin/products/${p.product_id}`, { base_price: val });
    toast.success(`${p.name}: prezzo base aggiornato`);
    load();
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.product_id}`);
    toast.success("Prodotto eliminato");
    load();
  };

  const uploadImage = async (p, file) => {
    if (!file) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    setUploading(p.product_id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/uploads/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = backend + r.data.url;
      await api.put(`/admin/products/${p.product_id}`, { image_url: fullUrl });
      toast.success("Immagine aggiornata");
      load();
    } catch (e) {
      toast.error("Upload fallito", { description: e?.response?.data?.detail || "Riprova." });
    } finally {
      setUploading(null);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-5 gap-3 flex-wrap">
        <Button
          onClick={syncToSeed}
          disabled={syncing}
          variant="outline"
          className="border-sky-200 text-[#032B44] hover:bg-sky-50"
          data-testid="sync-products-button"
        >
          {syncing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sincronizzazione…</> : <>↻ Sincronizza con catalogo aggiornato</>}
        </Button>
        <Button onClick={() => setCreating(true)} className="bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="new-product-button">
          <Plus className="w-4 h-4 mr-1.5" /> Nuovo prodotto
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => (
          <div key={p.product_id} className="bg-white border border-sky-100 rounded-2xl p-5" data-testid={`admin-product-${p.slug}`}>
            <div className="relative group mb-4">
              <img src={p.image_url} alt="" className="w-full aspect-[4/3] object-cover rounded-lg" />
              <label className={`absolute inset-0 rounded-lg bg-[#032B44]/0 group-hover:bg-[#032B44]/60 grid place-items-center transition-all cursor-pointer ${uploading === p.product_id ? "bg-[#032B44]/60" : ""}`} data-testid={`product-upload-${p.slug}`}>
                <div className={`flex flex-col items-center gap-1 text-white text-xs ${uploading === p.product_id ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity`}>
                  {uploading === p.product_id ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <><UploadCloud className="w-6 h-6" /><span>Cambia immagine</span></>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  disabled={uploading === p.product_id}
                  onChange={(e) => uploadImage(p, e.target.files?.[0])}
                />
              </label>
            </div>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-lg text-[#032B44] font-medium truncate">{p.name}</h3>
                <p className="text-xs text-slate-500 mb-3">{p.unit}</p>
              </div>
              <button onClick={() => deleteProduct(p)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`product-delete-${p.slug}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <label className="text-xs tracking-wider uppercase font-bold text-sky-600">Prezzo base (€)</label>
            <div className="flex gap-2 mt-1">
              <Input
                type="number"
                step="0.01"
                defaultValue={p.base_price.toFixed(2)}
                onBlur={(e) => {
                  if (parseFloat(e.target.value) !== p.base_price) updatePrice(p, e.target.value);
                }}
                className="h-10"
                data-testid={`product-price-input-${p.slug}`}
              />
            </div>
          </div>
        ))}
      </div>
      {creating && <NewProductDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </>
  );
};

const NewProductDialog = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "", slug: "", unit: "", description: "",
    base_price: "", sort_order: 10,
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const autoSlug = (name) => name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.unit || !form.base_price) {
      toast.error("Compila nome, unità e prezzo");
      return;
    }
    setSaving(true);
    try {
      let image_url = "https://images.unsplash.com/photo-1612924693632-b55d751457c6?crop=entropy&cs=srgb&fm=jpg&q=85";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/uploads/product-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        image_url = backend + r.data.url;
      }
      await api.post("/admin/products", {
        name: form.name,
        slug: form.slug || autoSlug(form.name),
        description: form.description || form.name,
        base_price: parseFloat(form.base_price),
        unit: form.unit,
        image_url,
        sort_order: parseInt(form.sort_order) || 10,
      });
      toast.success("Prodotto creato");
      onCreated();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail || "Riprova." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#032B44]">Nuovo prodotto</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Labelled label="Nome *"><Input value={form.name} onChange={upd("name")} required data-testid="new-product-name" /></Labelled>
            <Labelled label="Unità *"><Input value={form.unit} onChange={upd("unit")} placeholder="es. sacco 5kg" required data-testid="new-product-unit" /></Labelled>
            <Labelled label="Prezzo base (€) *"><Input type="number" step="0.01" value={form.base_price} onChange={upd("base_price")} required data-testid="new-product-price" /></Labelled>
            <Labelled label="Ordine"><Input type="number" value={form.sort_order} onChange={upd("sort_order")} data-testid="new-product-sort" /></Labelled>
          </div>
          <Labelled label="Descrizione">
            <textarea value={form.description} onChange={upd("description")} rows={2} className="w-full bg-white border border-sky-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#00A3FF] focus:border-transparent" data-testid="new-product-desc" />
          </Labelled>
          <Labelled label="Immagine (opzionale)">
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0])} className="text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-sky-200 file:bg-sky-50 file:text-[#032B44] file:text-sm hover:file:bg-sky-100" data-testid="new-product-image" />
          </Labelled>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-sky-200">Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-[#032B44] hover:bg-[#00A3FF] text-white" data-testid="new-product-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creazione…</> : "Crea prodotto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Labelled = ({ label, children }) => (
  <label className="block">
    <span className="text-xs tracking-wider uppercase font-bold text-sky-600 block mb-1.5">{label}</span>
    {children}
  </label>
);

const PartnersTab = () => {
  const [items, setItems] = useState([]);
  const [acting, setActing] = useState(null);
  const load = () => api.get("/admin/partner-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setActing(id);
    try {
      await api.post(`/admin/partner-requests/${id}/approve`, {});
      toast.success("Richiesta approvata");
      load();
    } finally { setActing(null); }
  };
  const reject = async (id) => {
    setActing(id);
    try {
      await api.post(`/admin/partner-requests/${id}/reject`);
      toast.success("Richiesta rifiutata");
      load();
    } finally { setActing(null); }
  };

  if (items.length === 0) {
    return (
      <div className="bg-sky-50/50 border border-sky-100 rounded-2xl p-12 text-center text-sm text-slate-500" data-testid="partners-empty">
        Nessuna richiesta partner al momento.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {items.map((it) => (
        <article key={it.request_id} className="bg-white border border-sky-100 rounded-2xl p-6" data-testid={`partner-request-${it.request_id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[250px]">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading text-lg font-medium text-[#032B44]">{it.company_name}</h3>
                <StatusPill status={it.status} />
              </div>
              <p className="text-xs text-slate-500">P.IVA: <span className="font-mono">{it.vat}</span></p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <p className="text-slate-700"><span className="text-slate-500">Referente:</span> {it.contact_name}</p>
                <p className="text-slate-700 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-sky-600" />
                  <a href={`mailto:${it.email}`} className="text-sky-700 hover:underline">{it.email}</a>
                </p>
                {it.phone && <p className="text-slate-700"><span className="text-slate-500">Tel:</span> {it.phone}</p>}
                {it.monthly_volume_kg && <p className="text-slate-700"><span className="text-slate-500">Volume:</span> {it.monthly_volume_kg} kg/mese</p>}
              </div>
              {it.message && <p className="text-sm text-slate-600 mt-3 italic">"{it.message}"</p>}
              <p className="text-xs text-slate-400 mt-3">{new Date(it.created_at).toLocaleString("it-IT")}</p>
            </div>
            {it.status === "pending" && (
              <div className="flex gap-2">
                <Button onClick={() => reject(it.request_id)} disabled={acting === it.request_id} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`partner-reject-${it.request_id}`}>
                  <X className="w-3.5 h-3.5 mr-1" /> Rifiuta
                </Button>
                <Button onClick={() => approve(it.request_id)} disabled={acting === it.request_id} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`partner-approve-${it.request_id}`}>
                  {acting === it.request_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                  Approva
                </Button>
              </div>
            )}
          </div>
        </article>
      ))}
    </div>
  );
};

const ScheduleTab = () => {
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [dates, setDates] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [courierLink, setCourierLink] = useState("");

  const load = (d) => {
    const iso = format(d, "yyyy-MM-dd");
    api.get(`/admin/delivery-schedule?date=${iso}`).then((r) => setData(r.data));
    api.get("/admin/delivery-dates").then((r) => setDates(r.data));
    setCourierLink("");
  };
  useEffect(() => { load(date); }, [date]);

  const change = (d) => { setDate(d); setPickerOpen(false); };

  const generateCourierLink = async () => {
    const iso = format(date, "yyyy-MM-dd");
    const r = await api.post(`/admin/delivery-schedule/${iso}/token`);
    const link = `${window.location.origin}/courier/${iso}?t=${r.data.token}`;
    setCourierLink(link);
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link corriere copiato", { description: "Incollalo su WhatsApp / SMS" });
    } catch {
      toast.success("Link generato");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => change(new Date())} className="border-sky-200" data-testid="schedule-today">Oggi</Button>
        <Button variant="outline" size="sm" onClick={() => change(addDays(new Date(), 1))} className="border-sky-200" data-testid="schedule-tomorrow">Domani</Button>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="border-sky-200" data-testid="schedule-date-picker">
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
              {format(date, "EEEE d MMMM yyyy", { locale: itLocale })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" locale={itLocale} selected={date} onSelect={(d) => d && change(d)} initialFocus />
          </PopoverContent>
        </Popover>
        <Button size="sm" onClick={generateCourierLink} className="bg-[#032B44] hover:bg-[#00A3FF] text-white ml-auto" data-testid="generate-courier-link">
          <Share2 className="w-3.5 h-3.5 mr-1.5" /> Link corriere
        </Button>
      </div>

      {courierLink && (
        <div className="bg-gradient-to-br from-[#032B44] to-[#00A3FF] text-white rounded-2xl p-5" data-testid="courier-link-box">
          <p className="text-xs tracking-[0.2em] uppercase font-bold opacity-80 mb-2">Link per il corriere</p>
          <div className="flex items-center gap-2 bg-white/10 rounded-lg p-2.5">
            <input value={courierLink} readOnly className="flex-1 bg-transparent text-xs font-mono outline-none truncate" />
            <button onClick={() => { navigator.clipboard.writeText(courierLink); toast.success("Copiato"); }} className="p-1.5 hover:bg-white/10 rounded" data-testid="copy-courier-link">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[11px] opacity-80 mt-2">
            Il corriere può aprirlo dal cellulare senza login: vedrà gli ordini, mappe e telefoni, e potrà segnare ogni consegna.
          </p>
        </div>
      )}

      {dates.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-slate-500">Prossime date con ordini:</span>
          {dates.slice(0, 7).map((d) => (
            <button
              key={d.date}
              onClick={() => change(new Date(d.date))}
              className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${format(date, "yyyy-MM-dd") === d.date ? "border-[#00A3FF] bg-sky-50 text-[#032B44] font-medium" : "border-sky-200 hover:bg-sky-50 text-slate-600"}`}
              data-testid={`upcoming-${d.date}`}
            >
              {format(new Date(d.date), "d MMM", { locale: itLocale })} · {d.count}
            </button>
          ))}
        </div>
      )}

      {!data ? (
        <div className="py-12 text-center text-sm text-slate-500">Caricamento…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SummaryCard icon={<Sun />} label="Mattina 08:00–12:30" count={data.mattina.count} total={data.mattina.total} />
            <SummaryCard icon={<Moon />} label="Pomeriggio 15:00–19:00" count={data.pomeriggio.count} total={data.pomeriggio.total} />
            <SummaryCard icon={<Truck />} label="Totale giornata" count={data.mattina.count + data.pomeriggio.count} total={data.grand_total} accent />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DeliveryList title="Mattina" icon={<Sun />} orders={data.mattina.orders} />
            <DeliveryList title="Pomeriggio" icon={<Moon />} orders={data.pomeriggio.orders} />
          </div>
        </>
      )}
    </div>
  );
};

const SummaryCard = ({ icon, label, count, total, accent }) => (
  <div className={`rounded-2xl p-5 border ${accent ? "border-sky-200 bg-gradient-to-br from-[#032B44] to-[#00A3FF] text-white" : "border-sky-100 bg-white"}`}>
    <div className="flex items-center gap-2 mb-2">
      {React.cloneElement(icon, { className: `w-4 h-4 ${accent ? "opacity-80" : "text-sky-600"}` })}
      <p className={`text-xs tracking-[0.2em] uppercase font-bold ${accent ? "opacity-90" : "text-sky-600"}`}>{label}</p>
    </div>
    <p className="font-heading text-3xl font-semibold">{count} <span className="text-sm font-normal opacity-80">ordini</span></p>
    <p className={`text-sm mt-1 ${accent ? "opacity-90" : "text-slate-600"}`}>{formatEUR(total)}</p>
  </div>
);

const DeliveryList = ({ title, icon, orders }) => (
  <section className="bg-white border border-sky-100 rounded-2xl p-5">
    <div className="flex items-center gap-2 mb-4">
      {React.cloneElement(icon, { className: "w-4 h-4 text-sky-600" })}
      <h3 className="font-heading text-lg font-medium text-[#032B44]">{title}</h3>
      <span className="text-xs text-slate-500 ml-auto">{orders.length} consegne</span>
    </div>
    {orders.length === 0 ? (
      <p className="text-sm text-slate-500 py-6 text-center">Nessuna consegna programmata.</p>
    ) : (
      <ul className="divide-y divide-sky-50">
        {orders.map((o) => (
          <li key={o.order_id} className="py-3" data-testid={`schedule-order-${o.order_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#032B44] text-sm truncate">{o.user_name}</p>
                <p className="text-xs text-slate-500 font-mono">{o.user_phone || o.user_email}</p>
                <p className="text-xs text-slate-600 mt-1">{o.delivery_address || "—"}</p>
                <p className="text-[11px] text-slate-500 mt-1">
                  {o.items.map((it) => `${it.name} × ${it.quantity}`).join(" · ")}
                </p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-[#032B44] text-sm">{formatEUR(o.total)}</p>
                <span className={`inline-block mt-1 text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 rounded-full border ${
                  o.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  o.status === "awaiting_delivery" ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                  "bg-amber-50 text-amber-700 border-amber-200"
                }`}>
                  {o.payment_method === "cash" ? "Contanti" : o.status === "paid" ? "Pagato" : "In attesa"}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const StatusPill = ({ status }) => {
  const m = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const lbl = { pending: "In attesa", approved: "Approvata", rejected: "Rifiutata" }[status] || status;
  return <span className={`text-[10px] tracking-wider uppercase font-bold px-2 py-0.5 rounded-full border ${m[status] || m.pending}`}>{lbl}</span>;
};

export default Admin;
